import ScreenPage from "../screens/screen-page"
import FinalScreenFrame from "../frames/final-screen-frame"
import LobbyFrame from "../frames/lobby-frame"
import Frame from "../frames/frame"
import { useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import PlayEnvironment from "../play-environment"
import playEnvironment from "../play-environment"
import { usePlayState } from "play/hooks/use-play-state"
import {
  serverNewClassroomGame,
  serverPlayReconnectTeacher,
} from "utils/server"
import { teacherReconnectSuccessfulReducer } from "play/reducers"
import { DEFAULT_DRAW_IMAGE } from "play/play-model"
import { hasText } from "utils/helpers"
import { startListeningForMessages } from "play/pending-messages"

interface Props {
  classId: string | null
  disableMemes: boolean
  enableTimer: boolean
  lessonId: string
}

interface FullProps extends Props {}

export default function TeacherPage() {
  const { lessonId } = useParams()
  return (
    <TeacherView
      lessonId={lessonId!}
      classId={PlayEnvironment.classId}
      enableTimer={PlayEnvironment.enableTimer}
      disableMemes={PlayEnvironment.disableMemes}
    />
  )
}

export function TeacherView(props: FullProps) {
  const { screen, screenIndex, teacherPageLoaded, modifyState } = usePlayState()
  const gameStarted = screenIndex > -1
  const { classId, disableMemes, enableTimer, lessonId } = props
  const isLoading = useRef(false)

  useEffect(() => {
    if (teacherPageLoaded) {
      return
    }
    if (isLoading.current) return
    isLoading.current = true

    modifyState((state) => {
      state.loadingReason = "Loading"
      state.internalPage = "Loading"
      state.isTeacherUrl = true
      state.lessonId = lessonId
      state.classId = classId
      state.disableMemes = disableMemes
      state.timer.visible = enableTimer
      state.enableGraphicalLogin = playEnvironment.enableGraphicalLogin
    })

    serverPlayReconnectTeacher({
      oldConnectionId: playEnvironment.connectionId!,
      classId: classId,
      enableGraphicalLogin: playEnvironment.enableGraphicalLogin,
      enableMemes: !disableMemes,
      lessonId: lessonId,
    })
      .then((resp) => {
        isLoading.current = false
        modifyState((state) => {
          teacherReconnectSuccessfulReducer(state, resp)
        })
      })
      .catch(() => {
        serverNewClassroomGame({
          lessonId: lessonId || "no-lesson-id",
          classId: classId,
          disableMemes: disableMemes,
          enableTimer: enableTimer,
          enableGraphicalLogin:
            !!playEnvironment.classId && playEnvironment.enableGraphicalLogin,
          shuffleAnswers: playEnvironment.shuffleAnswers,
          shuffleScreens: false,
        })
          .then((resp) =>
            modifyState((state) => {
              isLoading.current = false

              state.teacherPageLoaded = true
              state.teacherPageLoading = false

              state.connectionId = resp.connectionId
              state.teacherConnectionId = resp.connectionId
              state.studentsInLobby = resp.students
              state.gameCode = resp.gameCode
              state.imagesToPreload = [...resp.uniqueUrls, DEFAULT_DRAW_IMAGE]
              state.lessonName = resp.lessonName
              state.lessonImage =
                typeof resp.lessonImage !== "undefined"
                  ? resp.lessonImage
                  : null
              state.className = hasText(resp.className) ? resp.className : null
              state.internalPage = "Lobby"
              state.serverError = null

              playEnvironment.oldGameCode = state.gameCode
              playEnvironment.connectionId = state.connectionId

              startListeningForMessages({
                firstTime: true,
                hasTimer: resp.timerEnabledForSession,
              })
            })
          )
          .catch((e) => {
            isLoading.current = false

            modifyState((state) => {
              state.teacherPageLoaded = true
              state.teacherPageLoading = false
              state.internalPage = "Lobby"
              state.serverError = "FailedToStartClassroomGame"
            })
          })
      })
  }, [classId, lessonId, teacherPageLoaded])

  if (screen) {
    return (
      <Frame>
        <ScreenPage screen={screen} />
      </Frame>
    )
  } else {
    if (gameStarted) {
      return <FinalScreenFrame />
    } else {
      return <LobbyFrame />
    }
  }
}
